import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getBrandingConfig } from '../api/brandingSettings';
import { useTheme } from './ThemeContext';

/**
 * BrandingContext
 * Manages app-wide branding including logo, company name, and dynamic colors.
 * Applies CSS custom properties for colors when custom app colors are enabled.
 * Caches branding in localStorage for instant load on subsequent visits.
 */

const BrandingContext = createContext();

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

const BRANDING_CACHE_KEY = 'bms-branding-cache';

import { getCurrencySymbol } from '../utils/currency';
import { sidebarColours } from '../utils/brandColors';

// Default branding values
const defaultBranding = {
  company_name: 'Eldaved Global',
  company_email: 'info@company.com',
  company_phone: '+234 800 000 0000',
  company_address: '',
  currency: 'NGN',
  smart_decimals: true,
  timezone: 'Africa/Lagos',
  date_format: 'DD/MM/YYYY',
  logo_url: null,
  logo_type: 'icon',
  logo_icon_text: 'BMS',
  use_logo_as_watermark: false,
  watermark_opacity: 0.1,
  primary_color: '#1A1A1A',
  accent_color: '#D4AF37',
  text_color: '#333333',
  background_color: '#FFFFFF',
  sidebar_name_size: 'medium',
  // App UI colors
  app_primary_color: '#1A1A1A',
  app_accent_color: '#D4AF37',
  app_accent_light: '#FFD700',
  app_accent_dark: '#C5A353',
  app_success_color: '#10B981',
  app_warning_color: '#F59E0B',
  app_danger_color: '#EF4444',
  enable_custom_app_colors: false,
  // Wholesale module colors
  wholesale_primary_color: '#047857',
  wholesale_secondary_color: '#10B981',
  wholesale_accent_color: '#34D399',
  wholesale_currency: 'NGN',
  wholesale_smart_decimals: true
};

// Read cached branding from localStorage (instant, no network)
const getCachedBranding = () => {
  try {
    const cached = localStorage.getItem(BRANDING_CACHE_KEY);
    if (cached) return { ...defaultBranding, ...JSON.parse(cached) };
  } catch {
    /* corrupt cache — ignore */
  }
  return defaultBranding;
};

const saveBrandingCache = (config) => {
  try {
    localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(config));
  } catch {
    /* storage full — ignore */
  }
};

// Apply CSS custom properties to document root
const applyBrandColors = (config, theme, currentTheme) => {
  const root = document.documentElement;

  if (config.enable_custom_app_colors) {
    // Apply custom colors
    root.style.setProperty('--color-accent', config.app_accent_color || '#D4AF37');
    root.style.setProperty('--color-accent-light', config.app_accent_light || '#FFD700');
    root.style.setProperty('--color-accent-dark', config.app_accent_dark || '#C5A353');
    root.style.setProperty('--color-success', config.app_success_color || '#10B981');
    root.style.setProperty('--color-warning', config.app_warning_color || '#F59E0B');
    root.style.setProperty('--color-danger', config.app_danger_color || '#EF4444');

    // Apply accent color to Tailwind custom properties used in components
    root.style.setProperty('--accent-gold', config.app_accent_color || '#D4AF37');
    root.style.setProperty('--accent-gold-light', config.app_accent_light || '#FFD700');
    root.style.setProperty('--accent-gold-dark', config.app_accent_dark || '#C5A353');
  } else {
    // Reset to defaults
    root.style.setProperty('--color-accent', '#D4AF37');
    root.style.setProperty('--color-accent-light', '#FFD700');
    root.style.setProperty('--color-accent-dark', '#C5A353');
    root.style.setProperty('--color-success', '#10B981');
    root.style.setProperty('--color-warning', '#F59E0B');
    root.style.setProperty('--color-danger', '#EF4444');
    root.style.setProperty('--accent-gold', '#D4AF37');
    root.style.setProperty('--accent-gold-light', '#FFD700');
    root.style.setProperty('--accent-gold-dark', '#C5A353');
  }

  // Helper to determine if we should use dark theme fallback
  const useDarkThemeFallback = theme !== 'light' && currentTheme;

  // Retail colors (set in /settings/branding)
  const retailAccent =
    config.retail_accent_color || config.app_accent_color || config.accent_color || '#D4AF37';
  const retailPrimary =
    config.retail_primary_color || config.app_primary_color || config.primary_color || '#1A1A1A';
  const retailBorder = config.retail_border_color || retailPrimary;
  const retailHover = config.retail_hover_color || retailPrimary;
  const retailText = config.retail_text_color || '#E5E5E5';

  root.style.setProperty('--retail-primary', retailPrimary);
  root.style.setProperty('--retail-accent', retailAccent);
  root.style.setProperty('--retail-border', retailBorder);
  root.style.setProperty('--retail-hover', retailHover);
  root.style.setProperty('--retail-text', retailText);

  // Wholesale colors: its own (set in /wholesale/settings/branding), or the retail ones when
  // the wholesale module is set to use the retail branding colours
  const useRetailColors = config.enable_custom_wholesale_colors === false;
  if (useRetailColors) {
    root.style.setProperty('--wholesale-primary', retailPrimary);
    root.style.setProperty('--wholesale-secondary', retailBorder);
    root.style.setProperty('--wholesale-accent', retailAccent);
    root.style.setProperty('--wholesale-text', retailText);
  } else {
    const wholesaleAccent =
      config.wholesale_accent_color ||
      config.wholesale_secondary_color ||
      config.app_accent_color ||
      config.accent_color ||
      '#10B981';
    const wholesalePrimary =
      config.wholesale_primary_color ||
      config.app_primary_color ||
      config.primary_color ||
      '#047857';
    const wholesaleSecondary =
      config.wholesale_secondary_color || config.wholesale_border_color || wholesaleAccent;
    root.style.setProperty('--wholesale-primary', wholesalePrimary);
    root.style.setProperty('--wholesale-secondary', wholesaleSecondary);
    root.style.setProperty('--wholesale-accent', wholesaleAccent);
    root.style.setProperty('--wholesale-text', config.wholesale_text_color || '#D1FAE5');
  }

  // Sidebar surfaces: each user's theme, or the branding's sidebar colour blended with it
  // (recomputed when the theme changes). The accent is the brand's either way.
  const retailSidebar = sidebarColours(config, 'retail', theme);
  const wholesaleSidebar = sidebarColours(config, 'wholesale', theme);
  for (const [module, colours] of [
    ['retail', retailSidebar],
    ['wholesale', wholesaleSidebar]
  ]) {
    for (const [key, value] of Object.entries(colours)) {
      root.style.setProperty(`--${module}-sidebar-${key}`, value);
    }
  }
};

export const BrandingProvider = ({ children }) => {
  const { theme, currentTheme } = useTheme();
  // Initialise from localStorage cache for instant render (no branding flash)
  const [config, setConfig] = useState(getCachedBranding);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBranding = useCallback(async () => {
    try {
      // 1. Get tenant ID from storage or URL
      const storedTenant = localStorage.getItem('tenant');
      const tenant = storedTenant ? JSON.parse(storedTenant) : null;
      let tenantId = tenant?.id;

      if (!tenantId) {
        const searchParams = new URLSearchParams(window.location.search);
        tenantId = searchParams.get('tenantId');
      }

      const response = await getBrandingConfig(tenantId);
      const data = response?.data || response || defaultBranding;
      const merged = { ...defaultBranding, ...data };
      setConfig(merged);
      saveBrandingCache(merged); // persist for instant load next time
      setError(null);
    } catch (err) {
      console.warn('Could not fetch branding config, using defaults:', err?.message || err);
      // On error, keep whatever is in state (cached or default)
      setError(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Always fetch branding on mount — the API is public, no auth required
  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  // Apply colors whenever config or theme changes
  useEffect(() => {
    applyBrandColors(config, theme, currentTheme);
  }, [config, theme, currentTheme]);

  // Dynamically update the browser favicon and page title to match current branding
  useEffect(() => {
    let cancelled = false;

    /**
     * Sets the <link rel="icon"> href.
     */
    const setFaviconHref = (href, type = 'image/png') => {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.type = type;
      link.href = href;
    };

    /**
     * Loads an image URL onto an offscreen canvas, clips it as a circle,
     * and returns a PNG data URL — giving a rounded favicon in supported browsers.
     */
    const makeRoundFavicon = (url) =>
      new Promise((resolve, reject) => {
        const SIZE = 64; // Canvas size (px) — larger = crisper result
        const img = new Image();
        img.crossOrigin = 'anonymous'; // needed for external logo URLs
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = SIZE;
          canvas.height = SIZE;
          const ctx = canvas.getContext('2d');

          // Circular clipping path
          ctx.beginPath();
          ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();

          // Draw logo centred and scaled to fill the circle
          ctx.drawImage(img, 0, 0, SIZE, SIZE);

          resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = reject;
        img.src = url;
      });

    if (config.logo_url) {
      makeRoundFavicon(config.logo_url)
        .then((dataUrl) => {
          if (!cancelled) setFaviconHref(dataUrl, 'image/png');
        })
        .catch(() => {
          // If canvas conversion fails (e.g. CORS), fall back to raw URL
          if (!cancelled) setFaviconHref(config.logo_url);
        });
    } else {
      // No custom logo — use the default SVG favicon
      setFaviconHref('/vite.svg', 'image/svg+xml');
    }

    // Update the page title to the company name
    if (config.company_name) {
      document.title = config.company_name;
    }

    return () => {
      cancelled = true;
    };
  }, [config.logo_url, config.company_name]);

  // Function to refresh branding (call after saving settings)
  const refreshBranding = useCallback(async () => {
    await fetchBranding();
  }, [fetchBranding]);

  // Function to update config locally (for live preview)
  const updateConfigLocally = useCallback((updates) => {
    setConfig((prev) => {
      const updated = { ...prev, ...updates };
      saveBrandingCache(updated);
      return updated;
    });
  }, []);

  // Block rendering until branding is loaded from the database.
  // The localStorage cache makes subsequent loads near-instant.
  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#F0F2F5'
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            border: '3px solid #E5E7EB',
            borderTopColor: config.accent_color || '#D4AF37',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <BrandingContext.Provider
      value={{
        config,
        currency: config.currency || 'NGN',
        currencySymbol: getCurrencySymbol(config.currency || 'NGN'),
        loading,
        error,
        refreshBranding,
        updateConfigLocally
      }}
    >
      {children}
    </BrandingContext.Provider>
  );
};

export default BrandingContext;
